import { Trans, t } from "@lingui/macro";
import { X } from "lucide-react";
import { useEffect, useState } from "react";
import { useHistory } from "../../hooks/useRouterCompat";
import { useAppUIContext } from "../../context/AppUIContext";
import { Room } from "../../models/Room";
import { Fire } from "../../services/FireClient";
import { My } from "../../services/My";
import { UtilApp } from "../../services/Utils/UtilApp";
import { Util } from "../../services/Utils/Util";
import MessageItem, { MessageItemState } from "../components/MessageItem";
import { UIMessage } from "../components/UIMessage";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogClose,
} from "../../components/ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "../../components/ui/avatar";
import { cn } from "../../lib/utils";

const MessageModal: React.FC = () => {
  const [showModal, setShowModal] = useState(false);
  const [room, setRoom] = useState<Room>();
  const [message, setMessage] = useState<UIMessage>();
  const nothing = () => { };
  const { messageToShow, setMessageToShow } = useAppUIContext();
  const history = useHistory();
  const [inProgress, setInProgress] = useState(false);

  useEffect(() => {
    setMessage(undefined);
    setShowModal(!!messageToShow);
    initate()
  }, [messageToShow])

  async function initate() {
    if (messageToShow) {
      setRoom(My.Rooms.get(messageToShow.roomId));
      setMessage(new UIMessage(messageToShow, []));
      setInProgress(true);

      let parent = messageToShow.parentMessageId ? await Fire.msg.getMessage(messageToShow.parentMessageId) : messageToShow;
      if (parent) {
        let children = await Fire.msg.getMessagesByParentIds(My.user.id, [parent.id]);
        const sortedChildren = children.sort(((a, b) => a.created < b.created ? -1 : 1)).map(m => new UIMessage(m, []))
        const parentMsg = new UIMessage(parent, []);
        sortedChildren.forEach(c => {
          if (c.msg.emoticon && c.msg.replyToMessageId != parentMsg.msg.id) {
            const replyParent = sortedChildren.find(a => a.msg.id === c.msg.replyToMessageId);
            replyParent?.children.push(c);
          } else {
            parentMsg.children.push(c);
          }
        })
        setMessage(parentMsg);
      }
      setInProgress(false);
    }
  }

  function getMessageItemState(m: UIMessage): MessageItemState {
    return m.isNew() ? 'new' : 'old';
  }

  function gotoRoom() {
    setShowModal(false);
    history.replace(`/room/${room?.id}`);
    setMessageToShow(null);
  }

  function onOpenChange(open: boolean) {
    setShowModal(open);
    if (!open) {
      setMessageToShow(null);
    }
  }

  if (!messageToShow) return null;

  const replies = message?.nonReactions() ?? [];

  // Get unique reply users (up to 3)
  const replyUsers = replies.reduce((acc, reply) => {
    if (reply?.msg?.user && !acc.find(u => u.id === reply.msg.user.id)) {
      acc.push(reply.msg.user);
    }
    return acc;
  }, [] as { id: string; name: string; photoUrl: string | null }[]).slice(0, 3);

  return (
    <Dialog open={showModal} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] p-0 flex flex-col z-[60]">
        <DialogHeader className="px-6 py-4 border-b shrink-0 space-y-0">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={gotoRoom}
              className="text-sm font-medium hover:text-primary -ml-3"
              data-cy="close-notif"
            >
              {room && Room.getRoomNameWithSharp(room, My.user.id)}
            </Button>
          </div>
          {inProgress && (
            <div className="w-full h-1 bg-gray-200 overflow-hidden rounded-full mt-2">
              <div className="h-full w-full bg-primary animate-pulse" />
            </div>
          )}
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {message && (
            <div
              className={cn(
                "flex flex-col py-2 px-3 rounded-[12px] transition-all relative border bg-gray-100 border-slate-200 shadow-sm"
              )}
            >
              {/* Main Parent Message */}
              <div
                className={cn(
                  "message-box rounded-[8px] transition-colors p-2 border-2 border-transparent",
                  messageToShow.id === message.msg.id && "bg-orange-50 border-orange-300"
                )}
              >
                <MessageItem
                  m={message}
                  state={getMessageItemState(message)}
                  onMessageClick={nothing}
                  onResendMessage={() => { }}
                />
              </div>

              {/* Thread Indicator - Always expanded style */}
              {replies.length > 0 && (
                <div className="ml-2 xs:ml-[52px] mt-2">
                  {/* Thread header with reply count and users */}
                  <div className="flex items-center gap-3 py-1">
                    <div className="flex -space-x-1.5">
                      {replyUsers.map((user) => (
                        <Avatar key={user.id} className="w-5 h-5 border-2 border-white rounded-[6px]">
                          <AvatarImage src={user.photoUrl || Util.ANONYMOUS_PHOTO_URL} className="rounded-[6px]" />
                          <AvatarFallback className="text-[8px] font-semibold bg-orange-100 text-orange-600 rounded-[6px]">
                            {user.name?.[0]}
                          </AvatarFallback>
                        </Avatar>
                      ))}
                    </div>
                    <span className="text-xs font-bold text-orange-500">
                      {replies.length} {replies.length === 1 ? t`reply` : t`replies`}
                    </span>
                  </div>

                  {/* Thread Replies - Always visible */}
                  <div className="mt-3 pl-2 border-l-2 border-gray-200">
                    {replies.map((reply) => {
                      const isHighlighted = messageToShow.id === reply.msg.id;

                      return (
                        <div key={reply.msg.id} className="mb-3">
                          <div
                            className={cn(
                              "relative rounded-lg transition-colors border-2 border-transparent p-2",
                              isHighlighted && "bg-orange-50 border-orange-300"
                            )}
                          >
                            <MessageItem
                              m={reply}
                              state={getMessageItemState(reply)}
                              onMessageClick={nothing}
                              onResendMessage={() => { }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MessageModal;
